'use client';

import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';
import {
  type FieldProps,
  FieldTooltip,
  hasFieldProps,
  splitFieldProps,
} from '@/components/atoms/forms/_shared';
import {
  Field,
  FieldBottom,
  FieldBottomRight,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldTop,
  FieldTopRight,
} from '@/components/ui/form-field';
import {
  usePortalContainerOverride,
  usePortalReplacementRef,
} from '@/components/ui/portal-container';
import {
  guardExitingSelectKeys,
  SelectExitFocusContext,
  useSelectExitFocus,
  useSelectExitRef,
} from '@/components/ui/select-exit-guard';
import {
  SelectTriggerKeydownContext,
  useSelectItemRegistration,
  useSelectTriggerKeydown,
  useSelectTypeaheadDedup,
} from '@/components/ui/select-typeahead';
import { useControllableState } from '@/hooks/use-controllable-state';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

interface SelectRootProps
  extends Omit<
    React.ComponentProps<typeof SelectPrimitive.Root>,
    'value' | 'onValueChange' | 'items'
  > {
  /**
   * Optional custom trigger element that opens the select.
   * When provided, uses base-ui's render prop to prevent nested button hydration errors.
   * The trigger element will replace the default SelectTrigger entirely.
   * Note: You are responsible for rendering SelectValue inside your trigger to show the selected value.
   *
   * @example
   * <SelectRoot
   *   trigger={
   *     <Button appearance="outline" variant="secondary">
   *       <SelectValue placeholder="Choose..." />
   *     </Button>
   *   }
   * >
   *   <SelectPopup>...</SelectPopup>
   * </SelectRoot>
   */
  trigger?: React.ReactElement;
  /** Controlled value. When provided, component is controlled. */
  value?: string;
  /** Initial selected value for uncontrolled mode. */
  defaultValue?: string;
  /** Callback when selected value changes */
  onValueChange?: (value: string) => void;
  /**
   * Value→label map so Select.Value can display labels instead of raw values.
   * Auto-extracted from SelectItem children when omitted.
   */
  items?: Record<string, React.ReactNode>;
}

export type SelectPopupProps = SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'
  > & {
    /**
     * DOM node to portal the popup into. Falls back to
     * `usePortalContainerOverride()` (which checks `PortalContainerProvider`), then to
     * the base-ui default (the document body). Use this to keep the popup
     * inside a landmark (e.g. `<main>`) for accessibility tooling that
     * flags floating content rendered on the document body.
     */
    container?: HTMLElement | null;
  };

export type SelectGroupProps = SelectPrimitive.Group.Props;

export type SelectItemProps = SelectPrimitive.Item.Props;

export type SelectGroupLabelProps = SelectPrimitive.GroupLabel.Props;

export type SelectScrollDownButtonProps = React.ComponentProps<
  typeof SelectPrimitive.ScrollDownArrow
>;

export type SelectScrollUpButtonProps = React.ComponentProps<
  typeof SelectPrimitive.ScrollUpArrow
>;

export type SelectSeparatorProps = SelectPrimitive.Separator.Props;

export type SelectTriggerProps = SelectPrimitive.Trigger.Props & {
  /** Size variant matching Figma specs */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Shape variant */
  shape?: 'rounded' | 'round';
  /** Validation state */
  state?: 'default' | 'success' | 'error';
  /** Read-only mode renders as static text */
  readOnly?: boolean;
};

export interface SelectValueProps
  extends Omit<SelectPrimitive.Value.Props, 'children'> {
  placeholder?: string;
  children?: React.ReactNode | ((value: string | null) => React.ReactNode);
}

const selectTriggerSizeClasses = {
  xs: 'h-7 gap-0.5 px-2 py-0.5 text-sm [&_svg]:size-3.5 [@media(pointer:coarse)]:min-h-11',
  sm: 'h-8 gap-1 px-2.5 py-[5px] text-sm [&_svg]:size-4 [@media(pointer:coarse)]:min-h-11',
  md: 'h-9 gap-1.5 px-3 py-2 text-sm [&_svg]:size-5',
  lg: 'h-10 gap-2 px-4 py-[11px] text-sm [&_svg]:size-[22px]',
} as const;

export type SelectTriggerIconProps = React.HTMLAttributes<HTMLSpanElement>;

const SelectTriggerIcon = React.forwardRef<
  HTMLSpanElement,
  SelectTriggerIconProps
>(({ className, children, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="select-trigger-icon"
    className={cn('shrink-0 text-muted-foreground', className)}
    {...props}
  >
    {children}
  </span>
));
SelectTriggerIcon.displayName = 'SelectTrigger.Icon';

// ============================================
// Helpers
// ============================================

/** Recursively extract value→label entries from SelectItem children for base-ui label resolution. */
function extractItemsFromChildren(
  node: React.ReactNode,
): Record<string, React.ReactNode> | undefined {
  const map: Record<string, React.ReactNode> = {};
  let found = false;
  function traverse(n: React.ReactNode) {
    React.Children.forEach(n, (child) => {
      if (!React.isValidElement(child)) return;
      const p = child.props as { value?: unknown; children?: React.ReactNode };
      if (p.value != null) {
        map[String(p.value)] = p.children;
        found = true;
        return;
      }
      if (p.children != null) traverse(p.children);
    });
  }
  traverse(node);
  return found ? map : undefined;
}

/** Whether `value` is among the items passed to Base UI. Unknown items count as containing it. */
function itemsHaveValue(
  items: SelectRootProps['items'],
  value: string,
): boolean {
  return items == null || Object.hasOwn(items, value);
}

// ============================================
// Select (with internal state management)
// ============================================

function SelectRoot({
  trigger,
  value: controlledValue,
  defaultValue,
  onValueChange,
  items: itemsProp,
  children,
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  ...props
}: SelectRootProps) {
  const exitFocus = useSelectExitFocus({
    open,
    defaultOpen,
    onOpenChange,
    onOpenChangeComplete,
  });
  const [value, setValue] = useControllableState({
    value: controlledValue,
    defaultValue: defaultValue ?? '',
  });

  const { isDuplicate, isTypeahead, keydownContext } = useSelectTypeaheadDedup(
    exitFocus.context.trigger,
  );
  const items = itemsProp ?? extractItemsFromChildren(children);

  const handleValueChange = React.useCallback(
    (newValue: unknown, eventDetails?: { reason?: string; event?: Event }) => {
      // Base UI reconciles on its own (reason `none`, no user action) when the
      // held value vanishes from the items, e.g. options replaced while open:
      // it emits `null`, or the initial value when that is still an item.
      // That is not a user selection: show what Base UI shows (uncontrolled
      // only; a controlled parent owns `value`) and tell no one. Closed-trigger
      // typeahead also uses `none`. Item registration clears the keydown phase
      // before reconciliation; the trigger re-arms it before Base UI typeahead.
      const isReconciliation =
        typeof newValue !== 'string' ||
        (eventDetails?.reason === 'none' &&
          !keydownContext.inKeydown.current &&
          (!eventDetails.event || eventDetails.event.type === 'base-ui') &&
          !(keydownContext.itemValues.size > 0
            ? keydownContext.itemValues.has(value)
            : itemsHaveValue(items, value)));
      if (isReconciliation) {
        setValue(typeof newValue === 'string' ? newValue : '');
        return;
      }
      // A rejected controlled request is only deduplicated within this typing
      // session; it must never become the committed value.
      if (
        isTypeahead(eventDetails) &&
        (isDuplicate(newValue) || newValue === value)
      )
        return;
      setValue(newValue);
      onValueChange?.(newValue);
    },
    [
      items,
      value,
      setValue,
      onValueChange,
      keydownContext,
      isDuplicate,
      isTypeahead,
    ],
  );

  return (
    <SelectExitFocusContext.Provider value={exitFocus.context}>
      <SelectTriggerKeydownContext.Provider value={keydownContext}>
        <SelectPrimitive.Root
          value={value}
          onValueChange={handleValueChange}
          items={items}
          {...props}
          open={exitFocus.open}
          onOpenChange={exitFocus.onOpenChange}
          onOpenChangeComplete={exitFocus.onOpenChangeComplete}
        >
          {trigger && <SelectTrigger render={trigger} />}
          {children}
        </SelectPrimitive.Root>
      </SelectTriggerKeydownContext.Provider>
    </SelectExitFocusContext.Provider>
  );
}

// ============================================
// SelectGroup
// ============================================

const SelectGroup = React.forwardRef<HTMLDivElement, SelectGroupProps>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.Group
      ref={ref}
      data-slot="select-group"
      className={cn('scroll-my-1 p-1', className)}
      {...props}
    />
  ),
);
SelectGroup.displayName = 'Select.Group';

// ============================================
// SelectValue (with placeholder support)
// ============================================

const SelectValue = React.forwardRef<HTMLSpanElement, SelectValueProps>(
  ({ placeholder, children, className, ...props }, ref) => (
    <SelectPrimitive.Value
      ref={ref}
      data-slot="select-value"
      className={cn(
        'flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden text-left',
        className,
      )}
      placeholder={
        !children && placeholder ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : undefined
      }
      {...props}
    >
      {children}
    </SelectPrimitive.Value>
  ),
);
SelectValue.displayName = 'Select.Value';

// ============================================
// SelectTrigger
// ============================================

const SelectTrigger = React.forwardRef<HTMLElement, SelectTriggerProps>(
  (
    {
      className,
      size = 'md',
      shape = 'rounded',
      state = 'default',
      readOnly = false,
      children,
      'aria-label': ariaLabel,
      onKeyDownCapture,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const keydownProps = useSelectTriggerKeydown({
      onKeyDownCapture,
      onKeyDown,
    });
    const triggerRef = useSelectExitRef('trigger', ref);
    // Extract placeholder text from SelectValue children for accessible name fallback
    let resolvedAriaLabel = ariaLabel;
    if (!resolvedAriaLabel && !props['aria-labelledby']) {
      React.Children.forEach(children, (child) => {
        if (
          React.isValidElement(child) &&
          typeof child.props === 'object' &&
          child.props !== null &&
          'placeholder' in child.props &&
          typeof child.props.placeholder === 'string'
        ) {
          resolvedAriaLabel = child.props.placeholder;
        }
      });
    }
    // Read-only mode: render as semantic output element
    if (readOnly) {
      return (
        <output
          ref={ref as React.Ref<HTMLOutputElement>}
          data-slot="select-trigger"
          data-readonly
          data-size={size}
          data-shape={shape}
          className={cn(
            'flex w-full items-center border border-border bg-muted',
            selectTriggerSizeClasses[size],
            shape === 'rounded' && 'rounded-xl',
            shape === 'round' && 'rounded-full',
            'text-foreground',
            className,
          )}
        >
          {children}
        </output>
      );
    }

    return (
      <SelectPrimitive.Trigger
        ref={triggerRef}
        data-slot="select-trigger"
        data-size={size}
        data-shape={shape}
        data-state={state}
        aria-label={resolvedAriaLabel}
        aria-invalid={state === 'error' || undefined}
        className={cn(
          // Base layout
          'group/select flex w-full items-center justify-between outline-none',
          'border bg-background transition-[color,background-color,box-shadow,border-color] duration-200 ease-smooth motion-reduce:transition-none',
          'disabled:cursor-not-allowed disabled:border-input disabled:bg-muted',
          'data-[placeholder]:text-muted-foreground',
          // Size
          selectTriggerSizeClasses[size],
          // Shape
          shape === 'rounded' && 'rounded-xl',
          shape === 'round' && 'rounded-full',
          // State - border colors
          state === 'default' && 'border-input',
          state === 'success' && 'border-success',
          state === 'error' && 'border-destructive',
          // Focus ring
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          // Error state focus
          state === 'error' && 'focus-visible:ring-destructive/20',
          className,
        )}
        {...props}
        {...keydownProps}
      >
        {children}
        <SelectPrimitive.Icon
          render={
            <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-(--duration-fast) ease-standard group-data-[popup-open]/select:rotate-180 motion-reduce:transition-none" />
          }
        />
      </SelectPrimitive.Trigger>
    );
  },
);
SelectTrigger.displayName = 'Select.Trigger';

// ============================================
// SelectPopup
// ============================================

const SelectPopup = React.forwardRef<HTMLDivElement, SelectPopupProps>(
  (
    {
      className,
      children,
      side = 'bottom',
      sideOffset = 4,
      align = 'center',
      alignOffset = 0,
      alignItemWithTrigger = false,
      container,
      onKeyDownCapture,
      ...props
    },
    ref,
  ) => {
    const resolvedContainer = usePortalContainerOverride(container);
    const exitRef = useSelectExitRef('popup', ref, props.finalFocus);
    const { ref: popupRef, finalFocus } = usePortalReplacementRef(
      resolvedContainer,
      exitRef,
      undefined,
      props.finalFocus,
    );
    return (
      <SelectPrimitive.Portal container={resolvedContainer}>
        <SelectPrimitive.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          alignItemWithTrigger={alignItemWithTrigger}
          className="isolate z-50"
        >
          <SelectPrimitive.Popup
            ref={popupRef}
            data-slot="select-popup"
            className={cn(
              'bg-popover text-popover-foreground transition-opacity duration-(--duration-fast) ease-standard data-starting-style:opacity-0 data-ending-style:opacity-0 motion-reduce:transition-none motion-reduce:data-starting-style:opacity-100 ring-foreground/10 min-w-36 rounded-md shadow-md ring-1 relative isolate z-50 max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto',
              className,
            )}
            {...props}
            finalFocus={finalFocus}
            onKeyDownCapture={(event) => {
              onKeyDownCapture?.(event);
              guardExitingSelectKeys(event);
            }}
          >
            <SelectScrollUpButton />
            <SelectPrimitive.List>{children}</SelectPrimitive.List>
            <SelectScrollDownButton />
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    );
  },
);
SelectPopup.displayName = 'Select.Popup';

// ============================================
// SelectGroupLabel
// ============================================

const SelectGroupLabel = React.forwardRef<
  HTMLDivElement,
  SelectGroupLabelProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.GroupLabel
    ref={ref}
    data-slot="select-group-label"
    className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
    {...props}
  />
));
SelectGroupLabel.displayName = 'Select.GroupLabel';

// ============================================
// SelectItem
// ============================================

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, ...props }, ref) => {
    useSelectItemRegistration(props.value);
    return (
      <SelectPrimitive.Item
        ref={ref}
        data-slot="select-item"
        className={cn(
          "focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground gap-2 rounded-sm py-1.5 pe-8 ps-2 text-sm [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 relative flex w-full cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ItemText className="flex flex-1 gap-2 shrink-0 whitespace-nowrap">
          {children}
        </SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator
          render={
            <span className="pointer-events-none absolute end-2 flex size-4 items-center justify-center" />
          }
        >
          <Check className="pointer-events-none" />
        </SelectPrimitive.ItemIndicator>
      </SelectPrimitive.Item>
    );
  },
);
SelectItem.displayName = 'Select.Item';

// ============================================
// SelectSeparator
// ============================================

const SelectSeparator = React.forwardRef<HTMLDivElement, SelectSeparatorProps>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
      ref={ref}
      data-slot="select-separator"
      className={cn('bg-border -mx-1 my-1 h-px pointer-events-none', className)}
      {...props}
    />
  ),
);
SelectSeparator.displayName = 'Select.Separator';

// ============================================
// SelectScrollUpButton
// ============================================

const SelectScrollUpButton = React.forwardRef<
  HTMLDivElement,
  SelectScrollUpButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpArrow
    ref={ref}
    data-slot="select-scroll-up-button"
    className={cn(
      "bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4 top-0 w-full",
      className,
    )}
    {...props}
  >
    <ChevronUp />
  </SelectPrimitive.ScrollUpArrow>
));
SelectScrollUpButton.displayName = 'Select.ScrollUpButton';

// ============================================
// SelectScrollDownButton
// ============================================

const SelectScrollDownButton = React.forwardRef<
  HTMLDivElement,
  SelectScrollDownButtonProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownArrow
    ref={ref}
    data-slot="select-scroll-down-button"
    className={cn(
      "bg-popover z-10 flex cursor-default items-center justify-center py-1 [&_svg:not([class*='size-'])]:size-4 bottom-0 w-full",
      className,
    )}
    {...props}
  >
    <ChevronDown />
  </SelectPrimitive.ScrollDownArrow>
));
SelectScrollDownButton.displayName = 'Select.ScrollDownButton';

SelectRoot.displayName = 'SelectRoot';

// =============================================================================
// Flat Select API with smart field mode
// =============================================================================

export interface SelectProps
  extends FieldProps,
    Pick<SelectTriggerProps, 'aria-label' | 'aria-labelledby'> {
  /** Placeholder text for the trigger */
  placeholder?: string;
  /** Size variant matching Figma specs */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Shape variant */
  shape?: 'rounded' | 'round';
  /** Visual validation state from the Figma Select family */
  state?: 'default' | 'success' | 'error';
  /** Read-only mode renders as static text */
  readOnly?: boolean;
  /** Controlled value */
  value?: string;
  /** Initial selected value for uncontrolled mode */
  defaultValue?: string;
  /** Callback when selected value changes */
  onValueChange?: (value: string) => void;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** SelectItem children */
  children: React.ReactNode;
  /** Additional class name for the trigger */
  className?: string;
  /** Name attribute for form submission */
  name?: string;
}

function Select({
  placeholder,
  size = 'md',
  shape = 'rounded',
  state = 'default',
  readOnly = false,
  value,
  defaultValue,
  onValueChange,
  disabled,
  children,
  className,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: SelectProps) {
  const [fieldPropsObj, _leftover] = splitFieldProps(rest);

  if (!hasFieldProps(fieldPropsObj)) {
    return (
      <SelectRoot
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        name={name}
      >
        <SelectTrigger
          size={size}
          shape={shape}
          readOnly={readOnly}
          state={fieldPropsObj.error ? 'error' : state}
          className={className}
          aria-label={ariaLabel ?? placeholder}
          aria-labelledby={ariaLabelledBy}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        {!readOnly && <SelectPopup>{children}</SelectPopup>}
      </SelectRoot>
    );
  }

  const {
    label,
    error,
    description,
    tooltip,
    labelIcon,
    topRight,
    bottomRight,
    fieldClassName,
  } = fieldPropsObj;
  const hasTopRow = label || topRight;
  const hasBottomRow = description || bottomRight || error;

  return (
    <Field
      state={error ? 'destructive' : undefined}
      disabled={disabled}
      className={fieldClassName}
    >
      {hasTopRow && (
        <FieldTop>
          {label && (
            <FieldLabel required={fieldPropsObj.required} labelIcon={labelIcon}>
              {label}
              {tooltip && <FieldTooltip content={tooltip} />}
            </FieldLabel>
          )}
          {topRight && <FieldTopRight>{topRight}</FieldTopRight>}
        </FieldTop>
      )}
      <FieldControl>
        {(ctrlProps) => (
          <SelectRoot
            value={value}
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            disabled={disabled}
            name={name}
          >
            <SelectTrigger
              id={ctrlProps.id}
              aria-describedby={ctrlProps['aria-describedby']}
              aria-invalid={ctrlProps['aria-invalid']}
              size={size}
              shape={shape}
              readOnly={readOnly}
              state={error ? 'error' : state}
              className={className}
              aria-label={ariaLabel ?? placeholder}
              aria-labelledby={ariaLabelledBy}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            {!readOnly && <SelectPopup>{children}</SelectPopup>}
          </SelectRoot>
        )}
      </FieldControl>
      {hasBottomRow && (
        <FieldBottom>
          {error && <FieldError>{error}</FieldError>}
          {!error && description && (
            <FieldDescription>{description}</FieldDescription>
          )}
          {bottomRight && (
            <FieldBottomRight className="ms-auto">
              {bottomRight}
            </FieldBottomRight>
          )}
        </FieldBottom>
      )}
    </Field>
  );
}

Select.displayName = 'Select';

/**
 * Compound Select for advanced usage (custom triggers, popups, etc.)
 *
 * @example
 * ```tsx
 * <SelectCompound>
 *   <SelectTrigger>
 *     <SelectValue placeholder="Select an option" />
 *   </SelectTrigger>
 *   <SelectPopup>
 *     <SelectItem value="1">Option 1</SelectItem>
 *   </SelectPopup>
 * </SelectCompound>
 * ```
 */
const SelectCompound = Object.assign(SelectRoot, {
  Root: SelectRoot,
  Group: SelectGroup,
  Value: SelectValue,
  Trigger: Object.assign(SelectTrigger, { Icon: SelectTriggerIcon }),
  Popup: SelectPopup,
  GroupLabel: SelectGroupLabel,
  Item: SelectItem,
  Separator: SelectSeparator,
  ScrollUpButton: SelectScrollUpButton,
  ScrollDownButton: SelectScrollDownButton,
});

export {
  Select,
  SelectCompound,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
};
